import { ArrowClockwiseIcon } from '@phosphor-icons/react';
import { useCallback, useMemo } from 'react';
import toast from 'react-hot-toast';
import { Link, Outlet, useNavigate, useParams, useResolvedPath } from 'react-router-dom';
import { invariant } from '@utils/invariant';
import * as Yup from 'yup';

import { Breadcrumb } from '../../../../components/Breadcrumb';
import { Button } from '../../../../components/button/Button';
import { DataField } from '../../../../components/DataField';
import { ConfirmDialog } from '../../../../components/dialog/ConfirmDialog';
import { InputDialog } from '../../../../components/dialog/InputDialog';
import { PageHeader } from '../../../../components/PageHeader';
import { ITabItem, TabBar } from '../../../../components/tabs/Tabs';
import { LocationDocumentType } from 'src/types/enums';
import {
  type Location as LocationType,
  linkLocationDocument,
  unlinkLocationDocument,
  updateLocation,
  useLocationById,
} from '../../models';
import { InternalRole } from '../../../auth/endpoints/MeEndpoint.types';
import { getDisplayError } from '../../../../utils/get-display-error';
import { MultiFileUploaderButton } from '../../../document/components/MultiFileUploaderButton';
import { LocationProvider } from './locationContext';
import { useAuth } from '../../../../contexts/auth-context';
import { PageHeading } from 'components/PageHeading';
import { captureException } from '@sentry/react';
import { roleToNumber } from 'src/app/auth/roles.client';
import { ErrorState } from 'components/error-state/ErrorState';
import { DelayedSpinner } from 'components/Spinner';
import { SummaryBar } from 'components/SummaryBar';
import { ErrorStateBoundary } from 'components/error-state/ErrorStateBoundary';

export type Location = LocationType;

const LocationPage = () => {
  const { me } = useAuth();
  const navigate = useNavigate();
  const { locationId } = useParams<{ locationId: string }>();
  invariant(locationId);
  const { data, error, isLoading, mutate: reexecuteQuery } = useLocationById(+locationId);

  const generalRoute = useResolvedPath('general');
  const openingHoursRoute = useResolvedPath('opening-hours');
  const contactsRoute = useResolvedPath('contacts');
  const auditLogsRoute = useResolvedPath('audit-logs');
  const tabItems = useMemo<ITabItem[]>(() => {
    const res = [
      {
        title: 'Algemeen',
        path: generalRoute.pathname,
      },
      {
        title: 'Openingsuren',
        path: openingHoursRoute.pathname,
      },
      {
        title: 'Contacten',
        path: contactsRoute.pathname,
      },
    ];

    if (roleToNumber(me.role) >= roleToNumber(InternalRole.InternalAdmin)) {
      res.push({
        title: 'Audit logs',
        path: auditLogsRoute.pathname,
      });
    }

    return res;
  }, []);

  const refetchData = useCallback(() => {
    return reexecuteQuery();
  }, [reexecuteQuery]);

  const location = data;

  if (isLoading) {
    return <DelayedSpinner size={8} />;
  }

  if (!location) {
    return (
      <ErrorState
        error={error}
        title={error ? 'Locatie kon niet worden geladen' : 'Locatie niet gevonden'}
        description={error ? getDisplayError(error) : 'De gevraagde locatie werd niet gevonden.'}
        onRetry={error ? refetchData : undefined}
        variant="page"
      />
    );
  } else {
    return (
      <>
        <LocationProvider location={location} refreshData={refetchData}>
          <PageHeader title={location.name} />

          <PageHeading
            noMarginBottom
            leftSide={
              <Breadcrumb
                parentItem={{
                  name: 'Locaties',
                  to: '/internal/locations',
                }}
                currentItem={location.name}
              />
            }
            rightSide={
              <div className="flex gap-2">
                {location.deactivationReason ? (
                  <ConfirmDialog
                    triggerText="Heractiveer locatie"
                    title="Heractiveer locatie"
                    submitText="Heractiveer"
                    description="Ben je zeker dat je de locatie wilt heractiveren?"
                    onSubmit={async () => {
                      try {
                        await updateLocation(location.id, {
                          deactivationReason: null,
                        });
                        await refetchData();
                        toast.success('Locatie is geheractiveerd');
                      } catch (err) {
                        captureException(err);
                        toast.error('Locatie heractiveren mislukt: ' + getDisplayError(err));
                      }
                    }}
                  />
                ) : (
                  <InputDialog
                    triggerText="Deactiveer locatie"
                    title="Deactiveer locatie"
                    submitText="Deactiveer"
                    description="Ben je zeker dat je de locatie wilt deactiveren?"
                    labelText="Reden"
                    validation={Yup.string().min(5, 'Minimum 5 karakters')}
                    onSubmit={async (value: string) => {
                      try {
                        await updateLocation(location.id, {
                          deactivationReason: value,
                        });
                        toast.success('Locatie is gedeactiveerd');
                        navigate('..');
                      } catch (err) {
                        captureException(err);
                        toast.error('Locatie deactiveren mislukt: ' + getDisplayError(err));
                        throw err;
                      }
                    }}
                  />
                )}

                <MultiFileUploaderButton
                  buttonText="Andere"
                  title="Andere"
                  files={location.documents.filter((d) => d.type === LocationDocumentType.Other).map((v) => v.document)}
                  onSubmit={async (documents) => {
                    let count = 0;
                    await Promise.allSettled(
                      documents.map(async (doc) => {
                        try {
                          await linkLocationDocument(location.id, doc.id, LocationDocumentType.Other);
                          count += 1;
                          toast.success(`Bestand: ${doc.name}, ${count}/${documents.length} opgeladen`);
                        } catch (err) {
                          captureException(err);
                          toast.error(`Kon bestand ${doc.name} niet opladen: ${getDisplayError(err)}`);
                        }
                      }),
                    );
                    await refetchData();
                  }}
                  unlinkDocument={async (docId: string) => {
                    try {
                      await unlinkLocationDocument(location.id, docId, LocationDocumentType.Other);
                      await refetchData();
                      toast.success('Bestand gewist');
                    } catch (err) {
                      captureException(err);
                      toast.error(`Kon bestand niet wissen: ${getDisplayError(err)}`);
                    }
                  }}
                />

                <Button color="secondary" onTrigger={refetchData}>
                  <ArrowClockwiseIcon className="button-icon" />
                </Button>
              </div>
            }
          />

          <div className="shrink-0 bg-surface px-6">
            <TabBar items={tabItems} />
          </div>

          <div className="flex flex-col gap-4 p-7">
            {(!!location.customer || !!location.deactivationReason) && (
              <SummaryBar>
                {!!location.customer && (
                  <DataField title="Klant">
                    <Link to={`/internal/customers/${location.customer.id}/general`} className="link-text">
                      {location.customer.name}
                    </Link>
                  </DataField>
                )}
                {!!location.deactivationReason && (
                  <DataField title="Deactivatie reden">{location.deactivationReason}</DataField>
                )}
              </SummaryBar>
            )}

            <ErrorStateBoundary variant="section">
              <Outlet />
            </ErrorStateBoundary>
          </div>
        </LocationProvider>
      </>
    );
  }
};

export default LocationPage;
